import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { toThunkRejectValue } from "../../../../Common/isRequestCanceled";
import {
  BASE_URL,
  SALES_COUNT_CHART,
  REPORTS_BASE_URL,
  REPORTS_SALES_COUNT_API,
  IS_PY_GRAPHS,
} from "../../../../Constants/Config";
import { useAuthDetails } from "../../../../Common/cookiesHelper";

const initialState = {
  loading: true,
  salesCountData: [],
  totalSalesCount: 0,
  previousWeekSalesCount: 0,
  error: "",
  isCancel: false,
  lastRequestPayload: null,
};

let controller = null;

export const getSalesCount = createAsyncThunk(
  "salesCountChart/getSalesCount.",
  async (data, { rejectWithValue }) => {
    const { userTypeData } = useAuthDetails();
    if (controller) {
      controller.abort();
    }

    // Create a new AbortController instance for the current request
    controller = new AbortController();

    try {
      const { token, ...otherUserData } = userTypeData;
      const payload = { ...data };
      const activeType =
        payload?.activeType || payload?.view_by || payload?.date_type;

      const isDay = String(activeType || "").toLowerCase() === "day";
      const isWeek = String(activeType || "").toLowerCase() === "week";
      const dateInYYYYMMDD = (date) => {
        const year = date?.getFullYear();
        const month = String(date?.getMonth() + 1)?.padStart(2, "0");
        const day = String(date?.getDate())?.padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      const parseLocalYMD = (ymd) => {
        const parts = String(ymd || "").split("-");
        if (parts.length !== 3) return null;
        const y = Number(parts[0]);
        const m = Number(parts[1]);
        const d = Number(parts[2]);
        if (!y || !m || !d) return null;
        return new Date(y, m - 1, d);
      };

      const getLocalTodayYMD = () => {
        const now = new Date();
        return dateInYYYYMMDD(
          new Date(now.getFullYear(), now.getMonth(), now.getDate())
        );
      };

      const getWeekStartMonday = (date) => {
        const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const day = d.getDay(); // 0=Sun
        const diff = day === 0 ? -6 : 1 - day;
        d.setDate(d.getDate() + diff);
        d.setHours(0, 0, 0, 0);
        return d;
      };

      // ===== WEEK LOGIC =====
      if (isWeek) {
        const shouldUseToday = payload?.isViewingCurrentPeriod === true;
        const anchorYMD = shouldUseToday
          ? getLocalTodayYMD()
          : (payload?.end_date || payload?.start_date);
        const anchorDate = parseLocalYMD(anchorYMD) || new Date();

        const weekStart = getWeekStartMonday(anchorDate);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);

        const result = [];
        for (let i = 0; i < 7; i++) {
          const d = new Date(weekStart);
          d.setDate(d.getDate() + i);
          const formatted = dateInYYYYMMDD(d);
          result.push(`${formatted}_${formatted}`);
        }

        if (shouldUseToday) {
          payload.start_date = dateInYYYYMMDD(weekStart);
          payload.end_date = dateInYYYYMMDD(weekEnd);
        } else {
          payload.start_date = dateInYYYYMMDD(weekStart);
          payload.end_date = dateInYYYYMMDD(weekEnd);
        }

        payload.date_range = result.join(",");
        delete payload.new_date_range;
      }


      const apiUrl =
        IS_PY_GRAPHS === true || IS_PY_GRAPHS === "true"
          ? REPORTS_BASE_URL + REPORTS_SALES_COUNT_API
          : BASE_URL + SALES_COUNT_CHART;
      const response = await axios.post(
        apiUrl,
        { ...payload, ...otherUserData },
        {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
        }
      );
      let total_sales_count = 0;

      if (isWeek) {
        const resultArray = response?.data?.sale_count_data || [];
        console.log("resultArray", resultArray);
        let total = 0;
        if (Array.isArray(resultArray)) {
          resultArray.forEach((row) => {
            total += +row?.sale_count || 0;
          });
        }

        // const filter_revenue_data = dateKeys.map((d) => ({
        //   total_revenue: parseFloat((total || 0).toFixed(2)),
        //   sale_count: 0,
        // }));
        total_sales_count = total;
        return {
          ...response?.data,
          sale_count_data: resultArray,
          total_sale_count: total_sales_count,
        };
      }
      total_sales_count =
        response?.data?.total_sale_count ?? response?.data?.total_sales_count ?? 0;

      return {
        ...response?.data,
        total_sale_count: total_sales_count,
      };
    } catch (error) {
      if (axios.isCancel(error) || error.name === "CanceledError") {
        return rejectWithValue({ isCanceled: true }); // Mark as canceled
      }
      const customError = {
        message: toThunkRejectValue(error).message,
        status: toThunkRejectValue(error).status,
        data: error.response ? error.response.data : null,
      };
      return rejectWithValue(customError);
    }
  }
);

const salesCountChartSlice = createSlice({
  name: "salesCountChart",
  initialState,
  reducers: {
    clearSalesCountChart: (state, action) => {
      state.loading = true;
      state.salesCountData = [];
      state.totalSalesCount = 0;
      state.previousWeekSalesCount = 0;
      state.error = "";
      state.isCancel = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getSalesCount.pending, (state) => {
      state.loading = true;
      state.isCancel = false;
    });
    builder.addCase(getSalesCount.fulfilled, (state, action) => {
      state.loading = false;
      state.salesCountData = action.payload?.sale_count_data || [];
      state.totalSalesCount = action.payload?.total_sale_count || 0;
      state.previousWeekSalesCount = action.payload?.previous_sale_count || 0;
      state.error = "";
      state.isCancel = false;
      state.lastRequestPayload = action.meta?.arg || null;
    });
    builder.addCase(getSalesCount.rejected, (state, action) => {
      state.loading = false;
      state.salesCountData = [];
      state.totalSalesCount = 0;
      state.error = action.error.message;
      state.isCancel = action.payload.isCanceled;
      state.lastRequestPayload = action.meta?.arg || null;
    });
  },
});

export const { clearSalesCountChart } = salesCountChartSlice.actions;
export default salesCountChartSlice.reducer;
